import { useEffect, useRef, useState, type ReactNode } from "react";
import { Icon, OpsMark } from "./icons";
import { Input, cx } from "./ui";

/*
 * Amber everywhere the customer dashboard's shell is blue. Both consoles being
 * dark means the accent is the only thing left distinguishing them, so it is
 * used here without restraint — see docs/CONVENTIONS.md, "The one thing that
 * must not be aligned". Do not reconcile this with the customer palette.
 *
 * The two were structurally the same split layout until 2026-09-05, when the
 * customer dashboard's became a single centred column to match the community's
 * sign-in. This one still carries the panel on the left: nothing hands an
 * operator between this console and another product, so the argument that
 * removed the panel over there does not apply here.
 *
 * Extracted from LoginPage for the same reason the customer dashboard has one:
 * the layout is not the sign-in form's business, and a console that grows a
 * second unauthenticated screen should not grow a second copy of this.
 */

/** What this console is for. Every line is something it actually does. */
const POINTS = [
  "Register and manage the server fleet",
  "Support tenants, plans, and credit",
  "Follow every change in the audit log",
];

export function AuthShell({
  title,
  subtitle,
  children,
  footer,
  announce = false,
}: {
  title: string;
  subtitle: string;
  children: ReactNode;
  footer?: ReactNode;
  /** Used for a completed reset, where the submit control has disappeared. */
  announce?: boolean;
}) {
  const titleRef = useRef<HTMLHeadingElement>(null);

  useEffect(() => {
    const previousTitle = document.title;
    document.title = `${title} | Maxlayer Operations`;
    return () => {
      document.title = previousTitle;
    };
  }, [title]);

  useEffect(() => {
    if (!announce) return;
    const frame = requestAnimationFrame(() => titleRef.current?.focus());
    return () => cancelAnimationFrame(frame);
  }, [announce, title]);

  return (
    <div className="grid min-h-dvh bg-canvas lg:grid-cols-[1.1fr_1fr]">
      <aside className="relative isolate hidden overflow-hidden border-r border-line lg:block">
        {/* `grid-lines` used to sit here as well. Its mask is an ellipse anchored
            at the top centre, which on a panel this tall drew a hard rectangle
            of lattice in the corner and nothing anywhere else — texture where
            it was not wanted and none where it was. */}
        <div className="glow absolute inset-0 -z-20" aria-hidden="true" />

        {/* Logo at the top, the statement against the bottom, the light in
            between. The statement used to be centred in the remaining space,
            which left 315px of dead panel underneath it on a 900px screen —
            measured, and visible as the emptiest quarter of the page. Anchoring
            it closes that and gives the two columns a shared baseline. */}
        <div className="relative flex h-full flex-col justify-between p-12 xl:p-14">
          <div className="flex items-center gap-2.5">
            <OpsMark className="size-7" />
            <span className="text-[1.0625rem] font-medium tracking-[-0.022em] text-fg">
              Maxlayer Operations
            </span>
          </div>

          <div>
            {/*
              Deliberately not a product pitch. If someone lands here on the
              wrong bookmark, the page should say so before they start typing.
            */}
            <h2 className="max-w-xl text-display">
              Internal console.
              <br />
              <span className="gradient-text">Staff accounts only.</span>
            </h2>

            <ul className="mt-9 space-y-3.5">
              {POINTS.map((point) => (
                <li key={point} className="flex items-start gap-3 text-sm text-fg-muted">
                  <Icon name="shield" className="mt-0.5 size-4 shrink-0 text-accent" />
                  {point}
                </li>
              ))}
            </ul>
          </div>
        </div>
      </aside>

      <main
        aria-labelledby="auth-title"
        className="flex min-h-dvh items-start justify-center px-5 py-8 sm:items-center sm:px-8 sm:py-14"
      >
        {/* 25rem rather than `max-w-sm`. The controls grew to 44px and a 24rem
            column around them reads as a form that outgrew its box. */}
        <div className="w-full max-w-[25rem]">
          <div className="flex items-center justify-center gap-2.5 lg:hidden">
            <OpsMark className="size-9" />
            <span className="text-left leading-tight">
              <span className="block font-medium tracking-[-0.022em] text-fg">Maxlayer</span>
              <span className="block text-[0.625rem] font-medium tracking-[0.14em] text-accent uppercase">
                Operations
              </span>
            </span>
          </div>

          <p className="micro mt-8 text-center text-accent lg:mt-0 lg:text-left">Staff access</p>
          <h1
            ref={titleRef}
            id="auth-title"
            tabIndex={announce ? -1 : undefined}
            className="mt-4 text-center text-title lg:text-left"
          >
            {title}
          </h1>
          <p className="mt-3 text-center text-sm leading-6 text-fg-muted lg:text-left">
            {subtitle}
          </p>

          {/* No panel. The form is the only thing in this column, so a box
              around it was drawing a border between the page and its own
              contents — and on a near-black canvas that box is a second
              rectangle competing with the fields inside it. What separates the
              form from the page now is space. */}
          <div className="mt-9 sm:mt-10">{children}</div>

          {footer && (
            <div className="mt-8 text-center text-sm text-fg-muted lg:text-left">{footer}</div>
          )}
        </div>
      </main>
    </div>
  );
}

/** A six-digit authenticator code or a hyphenated recovery code. */
export function CodeInput({
  id,
  label,
  value,
  onChange,
  numeric = true,
  disabled,
  autoFocus,
}: {
  id: string;
  label: string;
  value: string;
  onChange: (value: string) => void;
  numeric?: boolean;
  disabled?: boolean;
  autoFocus?: boolean;
}) {
  return (
    <>
      <label htmlFor={id} className="sr-only">
        {label}
      </label>
      <input
        id={id}
        type="text"
        inputMode={numeric ? "numeric" : "text"}
        autoComplete="one-time-code"
        spellCheck={false}
        autoFocus={autoFocus}
        disabled={disabled}
        required
        maxLength={numeric ? 6 : 40}
        value={value}
        onChange={(event) =>
          onChange(
            numeric
              ? event.target.value.replace(/\D/g, "").slice(0, 6)
              : event.target.value.trimStart(),
          )
        }
        placeholder={numeric ? "000000" : "recovery code"}
        className={cx(
          "w-full rounded-md border border-line bg-surface py-3.5 text-center font-mono text-fg transition-colors duration-200 ease-smooth placeholder:text-fg-subtle focus:border-accent focus:ring-2 focus:ring-accent/25 focus:outline-none disabled:cursor-not-allowed disabled:text-fg-subtle",
          numeric ? "text-xl tracking-[0.4em] indent-[0.4em]" : "text-base",
        )}
      />
    </>
  );
}

/** Password field with a show/hide toggle and a caps-lock warning. */
export function PasswordInput({
  id,
  autoComplete,
  value,
  onChange,
  minLength,
  size = "xl",
  surface = "canvas",
}: {
  id: string;
  autoComplete: string;
  value: string;
  onChange: (event: React.ChangeEvent<HTMLInputElement>) => void;
  minLength?: number;
  /** Both default to the auth surface, because auth is the only caller. */
  size?: "md" | "xl";
  surface?: "panel" | "canvas";
}) {
  const [visible, setVisible] = useState(false);
  const [capsLock, setCapsLock] = useState(false);

  /*
   * Read from the keyboard event rather than tracked as state across
   * keystrokes: `getModifierState` is the browser's own answer, so it stays
   * right when caps lock is toggled while the field is not focused.
   */
  function checkCapsLock(event: React.KeyboardEvent<HTMLInputElement>) {
    setCapsLock(event.getModifierState("CapsLock"));
  }

  return (
    <div className="space-y-1.5">
      <div className="relative">
        <Input
          id={id}
          size={size}
          surface={surface}
          type={visible ? "text" : "password"}
          autoComplete={autoComplete}
          required
          minLength={minLength}
          value={value}
          onChange={onChange}
          onKeyUp={checkCapsLock}
          onKeyDown={checkCapsLock}
          onBlur={() => setCapsLock(false)}
          className="pr-20"
        />
        <button
          type="button"
          onClick={() => setVisible((current) => !current)}
          aria-label={visible ? "Hide password" : "Show password"}
          className="absolute inset-y-0 right-0 flex items-center gap-1.5 pr-3 pl-2 text-xs text-fg-subtle transition-colors duration-200 ease-smooth hover:text-fg"
        >
          <Icon name={visible ? "eye-off" : "eye"} className="size-4" />
          {visible ? "Hide" : "Show"}
        </button>
      </div>

      {/*
        Not coloured, unlike the customer dashboard's, which sets this amber.
        Amber is this console's accent, so an amber caution would read as
        branding rather than as a warning. The icon carries the signal and
        there is no `warn` token here to reach for.
      */}
      {capsLock && (
        <p role="status" className="flex items-center gap-1.5 text-xs text-fg-muted">
          <Icon name="warning" className="size-3.5 shrink-0" />
          Caps Lock is on.
        </p>
      )}
    </div>
  );
}
